import dayjs from "dayjs";
import { Formik } from "formik";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { DATE_FORMAT, INSURANCE_PLAN_RISKS } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import validation from "./validation";

const ServiceDetailView = ({ onSubmit, data = {}, cancel }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const [insurancePlans, setInsurancePlans] = useState([]);

  useQuery(INSURANCE_PLANS, {
    variables: {
      where: { risk: INSURANCE_PLAN_RISKS.SERVICE },
      take: null,
    },
    onCompleted: ({ insurancePlans: { data: plans } }) => {
      setInsurancePlans(
        plans.map(({ id, name }) => ({ value: id, label: name })),
      );
    },
  });

  return (
    <Formik
      initialValues={{
        name: data.name || "",
        insurancePlan: {
          id: get(data, "insurancePlan.id", ""),
          name: get(data, "insurancePlan.name", ""),
        },
        prices: {
          amount: get(data, "prices.amount", ""),
        },
        dates:
          data.startDate && data.endDate
            ? [dayjs(data.startDate), dayjs(data.endDate)]
            : [],
      }}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
      onSubmit={onSubmit}
    >
      {({ isValid, handleSubmit, setFieldValue, values, ...formProps }) => (
        <>
          <Header
            title={DETAIL.HEADER.TITLE}
            subtitle={DETAIL.HEADER.ACTION}
            actions={[
              {
                text: DETAIL.HEADER.DISCARD,
                onClick: cancel,
                type: "primary--outlined",
                icon: <BiChevronLeft size={20} />,
              },
              {
                text: DETAIL.HEADER.SAVE,
                onClick: handleSubmit,
                type: "primary",
                icon: <BiCheck size={20} />,
              },
            ]}
          />
          <Card title={FORM.GENERAL_INFO}>
            <div className="grid--2">
              <Select
                options={insurancePlans}
                placeholder={FORM.INSURANCE_PLAN}
                label={FORM.INSURANCE_PLAN}
                value={get(values, "insurancePlan.id", "")}
                {...bindInputProps({
                  name: "insurancePlan.id",
                  values,
                  ...formProps,
                })}
                onChange={(id) => setFieldValue("insurancePlan.id", id || "")}
              />
              <Input
                label={FORM.NAME}
                placeholder={FORM.NAME}
                {...bindInputProps({ name: "name", values, ...formProps })}
              />
              <Input
                type="date"
                variant="range"
                label={FORM.DATE}
                format={DATE_FORMAT}
                placeholders={[FORM.START_DATE, FORM.END_DATE]}
                {...bindInputProps({ name: "dates", values, ...formProps })}
                onChange={(value) => setFieldValue("dates", value)}
              />
            </div>
          </Card>

          <div className="m-top-24" />

          <Card type="simple" head title={FORM.PRICES}>
            <div className="grid--1">
              <Input
                type="number"
                label={FORM.FIXED_PRICE}
                placeholder={FORM.FIXED_PRICE}
                {...bindInputProps({
                  name: "prices.amount",
                  values,
                  ...formProps,
                })}
              />
            </div>
          </Card>
        </>
      )}
    </Formik>
  );
};

export default ServiceDetailView;
